// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { Children } from 'react';
import classNames from 'classnames';
import Dropdown from '../Dropdown';
import Icon from '../Icon';
import Menu, { MenuItem } from '../Menu';
import Tab from './Tab';
import TabClose from './TabClose';
import TabIcon from './TabIcon';
import TabLabel from './TabLabel';
import TabListItem from './TabListItem';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const TabList = _ref => {
  let {
    children,
    tabListRef,
    active,
    onTabClick,
    visibleLength,
    monospacedWidth,
    baseClassName,
    tabPanelId,
    idPrefix
  } = _ref;
  const handleTabClick = (tab, position) => {
    onTabClick(position);
    tab?.props?.onActivate?.();
  };
  const childArray = Children.map(children, (child, index) => ({
    child,
    position: index + 1
  }));
  const tabs = childArray.filter(_ref2 => {
    let {
      child
    } = _ref2;
    return child?.type === Tab;
  });
  const listChildren = tabs.slice(0, visibleLength);
  const menuChildren = tabs.slice(visibleLength);
  const listItems = listChildren.map(_ref3 => {
    let {
      child: tab,
      position
    } = _ref3;
    return /*#__PURE__*/_jsx(TabListItem, {
      tab: tab,
      baseClassName: baseClassName,
      selected: position === active,
      onClick: () => handleTabClick(tab, position),
      monospacedWidth: monospacedWidth,
      tabPanelId: tabPanelId,
      id: idPrefix ? `${idPrefix}-${position}` : undefined
    }, position);
  });
  const menuItems = menuChildren.map(_ref4 => {
    let {
      child: tab,
      position
    } = _ref4;
    const {
      title,
      label,
      icon,
      onActivate,
      onClose,
      component: Tag = 'a',
      ...tabProps
    } = tab.props;
    return /*#__PURE__*/_jsx(MenuItem, {
      icon: /*#__PURE__*/_jsx(TabIcon, {
        icon: icon,
        baseClassName: baseClassName
      }),
      label: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(TabLabel, {
          label: label,
          tabMode: "menu-item",
          baseClassName: baseClassName
        }), /*#__PURE__*/_jsx(TabClose, {
          onClose: onClose,
          tabMode: "menu-item",
          baseClassName: baseClassName
        })]
      }),
      active: active === position,
      onClick: () => handleTabClick(tab, position),
      className: `${baseClassName}__menu-item`,
      component: Tag,
      ...tabProps,
      children: title
    }, position);
  });
  const moreSelected = active > listChildren.length;
  return /*#__PURE__*/_jsxs("ul", {
    ref: tabListRef,
    className: `${baseClassName}__tab-list`,
    role: "tablist",
    "aria-labelledby": tabPanelId,
    children: [listItems, menuItems.length > 0 && /*#__PURE__*/_jsx("li", {
      className: classNames(`${baseClassName}__tab`, moreSelected && `${baseClassName}__tab--selected`, `${baseClassName}__tab--more`),
      role: "presentation",
      "aria-selected": moreSelected || undefined,
      children: /*#__PURE__*/_jsx(Dropdown, {
        className: `${baseClassName}__dropdown`,
        menu: /*#__PURE__*/_jsx(Menu, {
          children: menuItems
        }),
        menuPlacement: "bottom-end",
        children: /*#__PURE__*/_jsx("a", {
          className: `${baseClassName}__link`,
          role: "tab",
          tabIndex: 0,
          "aria-selected": moreSelected || undefined,
          onKeyDown: e => {
            if (e.key === 'Enter') {
              e.currentTarget.click();
            }
          },
          children: /*#__PURE__*/_jsx(Icon, {
            name: "three-dots-horizontal"
          })
        })
      })
    })]
  });
};
export default TabList;